import { useState, useRef, useEffect } from "react";

const SYSTEM_PROMPT = `You are Aria, a friendly and helpful AI assistant for websites and apps. You help users with their questions in a clear, warm, and concise way. Keep responses relatively brief (2-4 sentences unless more detail is needed). Be conversational and approachable.`;

const suggestions = [
    "What can you help me with?",
    "Tell me something interesting!",
    "How do I get started?",
];

function TypingIndicator() {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "10px 14px", background: "rgba(255,255,255,0.06)", borderRadius: 18, width: "fit-content", marginBottom: 8 }}>
            {[0, 1, 2].map(i => (
                <span key={i} style={{
                    width: 7, height: 7, borderRadius: "50%", background: "#a78bfa",
                    animation: "bounce 1.2s infinite", animationDelay: `${i * 0.2}s`,
                    display: "inline-block"
                }} />
            ))}
        </div>
    );
}

export default function ChatBot() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Hi! I'm Aria ✨ Your AI assistant. How can I help you today?" }
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 300);
    }, [open]);

    async function sendMessage(text) {
        if (!text.trim() || loading) return;
        setShowSuggestions(false);
        const userMsg = { role: "user", content: text };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInput("");
        setLoading(true);

        try {
            const res = await fetch("https://api.anthropic.com/v1/messages", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": "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",        // 👈 Your Anthropic key here
                    "anthropic-version": "2023-06-01",
                    "anthropic-dangerous-direct-browser-access": "true"
                },
                body: JSON.stringify({
                    model: "claude-sonnet-4-20250514",
                    max_tokens: 1000,
                    system: SYSTEM_PROMPT,
                    messages: newMessages.map(m => ({ role: m.role, content: m.content }))
                })
            });
            const data = await res.json();
            const reply = data.content?.[0]?.text || "Sorry, I couldn't get a response.";
            setMessages(prev => [...prev, { role: "assistant", content: reply }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: "assistant", content: "Oops! Something went wrong. Please try again." }]);
        }
        setLoading(false);
    }

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          background: #0a0a12; 
          min-height: 100vh; 
          font-family: 'DM Sans', sans-serif;
          display: flex; align-items: center; justify-content: center;
        }
        @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(139,92,246,0.4)} 50%{box-shadow:0 0 0 12px rgba(139,92,246,0)} }
        @keyframes slideIn { from{opacity:0;transform:translateY(30px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes msgIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(139,92,246,0.3); border-radius: 2px; }
      `}</style>

            {/* Background */}
            <div style={{
                position: "fixed", inset: 0, zIndex: 0,
                background: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(88,28,220,0.25) 0%, transparent 60%), #0a0a12"
            }} />

            {/* Demo landing content */}
            <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 24px", maxWidth: 600 }}>
                <div style={{
                    display: "inline-block", background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)",
                    borderRadius: 100, padding: "6px 16px", marginBottom: 24,
                    fontFamily: "'Syne', sans-serif", fontSize: 12, letterSpacing: 2, color: "#a78bfa", textTransform: "uppercase"
                }}>AI Chatbot Demo</div>
                <h1 style={{
                    fontFamily: "'Syne', sans-serif", fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 800,
                    color: "#fff", lineHeight: 1.1, marginBottom: 20,
                    textShadow: "0 0 80px rgba(139,92,246,0.5)"
                }}>Meet Aria,<br /><span style={{ color: "#a78bfa" }}>your AI assistant</span></h1>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 16, lineHeight: 1.7, marginBottom: 40 }}>
                    A beautifully designed chatbot widget you can embed on any website or app. Click the button below to start chatting.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    {["⚡ Powered by Claude", "🎨 Fully Customizable", "🔌 Easy to Embed"].map(tag => (
                        <span key={tag} style={{
                            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8, padding: "8px 14px", color: "rgba(255,255,255,0.6)", fontSize: 13
                        }}>{tag}</span>
                    ))}
                </div>
            </div>

            {/* Chat Window */}
            {open && (
                <div style={{
                    position: "fixed", bottom: 90, right: 24, width: "min(380px, calc(100vw - 48px))",
                    height: "min(560px, calc(100vh - 130px))",
                    background: "rgba(14,14,24,0.97)",
                    backdropFilter: "blur(20px)",
                    borderRadius: 24, border: "1px solid rgba(139,92,246,0.2)",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.08)",
                    display: "flex", flexDirection: "column", zIndex: 1000,
                    animation: "slideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)"
                }}>
                    {/* Header */}
                    <div style={{
                        padding: "18px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
                        background: "linear-gradient(135deg, rgba(88,28,220,0.2), rgba(139,92,246,0.1))",
                        borderRadius: "24px 24px 0 0", display: "flex", alignItems: "center", gap: 12
                    }}>
                        <div style={{ position: "relative" }}>
                            <div style={{
                                width: 42, height: 42, borderRadius: "50%",
                                background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 20, boxShadow: "0 0 20px rgba(139,92,246,0.4)"
                            }}>✨</div>
                            <div style={{
                                position: "absolute", bottom: 2, right: 2, width: 10, height: 10,
                                background: "#22c55e", borderRadius: "50%", border: "2px solid #0e0e18"
                            }} />
                        </div>
                        <div>
                            <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, color: "#fff", fontSize: 15 }}>Aria</div>
                            <div style={{ fontSize: 11, color: "#22c55e", fontWeight: 500 }}>● Online · Always here to help</div>
                        </div>
                        <button onClick={() => setOpen(false)} style={{
                            marginLeft: "auto", background: "rgba(255,255,255,0.08)", border: "none",
                            width: 32, height: 32, borderRadius: "50%", cursor: "pointer",
                            color: "rgba(255,255,255,0.6)", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all 0.2s"
                        }} onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.15)"}
                            onMouseLeave={e => e.target.style.background = "rgba(255,255,255,0.08)"}>✕</button>
                    </div>

                    {/* Messages */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px" }}>
                        {messages.map((msg, i) => (
                            <div key={i} style={{
                                display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                                marginBottom: 10, animation: "msgIn 0.25s ease"
                            }}>
                                {msg.role === "assistant" && (
                                    <div style={{
                                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginRight: 8, marginTop: 2,
                                        background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13
                                    }}>✨</div>
                                )}
                                <div style={{
                                    maxWidth: "75%", padding: "10px 14px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                                    background: msg.role === "user"
                                        ? "linear-gradient(135deg, #7c3aed, #6d28d9)"
                                        : "rgba(255,255,255,0.07)",
                                    border: msg.role === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                                    color: "#fff", fontSize: 14, lineHeight: 1.6,
                                    boxShadow: msg.role === "user" ? "0 4px 20px rgba(124,58,237,0.3)" : "none"
                                }}>{msg.content}</div>
                            </div>
                        ))}
                        {loading && (
                            <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginRight: 8,
                                    background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13
                                }}>✨</div>
                                <TypingIndicator />
                            </div>
                        )}
                        {showSuggestions && !loading && messages.length === 1 && (
                            <div style={{ marginTop: 12, animation: "fadeUp 0.4s ease" }}>
                                <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Quick starts</p>
                                {suggestions.map(s => (
                                    <button key={s} onClick={() => sendMessage(s)} style={{
                                        display: "block", width: "100%", textAlign: "left", background: "rgba(139,92,246,0.08)",
                                        border: "1px solid rgba(139,92,246,0.2)", borderRadius: 12, padding: "9px 14px",
                                        color: "#c4b5fd", fontSize: 13, cursor: "pointer", marginBottom: 6, transition: "all 0.2s"
                                    }}
                                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,92,246,0.18)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)"; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(139,92,246,0.08)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.2)"; }}
                                    >{s}</button>
                                ))}
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div style={{ padding: "12px 16px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{
                            display: "flex", gap: 8, background: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "8px 8px 8px 14px",
                        }}>
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
                                placeholder="Type a message..."
                                style={{
                                    flex: 1, background: "none", border: "none", outline: "none",
                                    color: "#fff", fontSize: 14, fontFamily: "'DM Sans', sans-serif"
                                }}
                            />
                            <button
                                onClick={() => sendMessage(input)}
                                disabled={!input.trim() || loading}
                                style={{
                                    width: 36, height: 36, borderRadius: 10, border: "none", cursor: "pointer",
                                    background: input.trim() && !loading ? "linear-gradient(135deg, #7c3aed, #a78bfa)" : "rgba(255,255,255,0.08)",
                                    color: "#fff", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                                    transition: "all 0.2s", flexShrink: 0,
                                    boxShadow: input.trim() && !loading ? "0 4px 15px rgba(124,58,237,0.4)" : "none"
                                }}
                            >➤</button>
                        </div>
                        <p style={{ textAlign: "center", fontSize: 10, color: "rgba(255,255,255,0.2)", marginTop: 8 }}>
                            Powered by Claude · Built with Anthropic API
                        </p>
                    </div>
                </div>
            )}

            {/* FAB Button */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    position: "fixed", bottom: 24, right: 24, width: 60, height: 60, borderRadius: "50%",
                    background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                    border: "none", cursor: "pointer", zIndex: 1001,
                    boxShadow: "0 8px 30px rgba(124,58,237,0.5)",
                    animation: "pulse 2s infinite",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: open ? 22 : 26, transition: "transform 0.3s",
                    transform: open ? "rotate(90deg) scale(1.05)" : "rotate(0) scale(1)"
                }}
            >{open ? "✕" : "💬"}</button>
        </>
    );
}